export type Language = 'en' | 'es' | 'zh';

export interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

export const translations: Record<Language, any> = {
  en: {
    // Settings
    settings: 'Settings',
    managePreferences: 'Manage your app preferences',
    appearance: 'Appearance',
    theme: 'Theme',
    chooseTheme: 'Choose your preferred theme',
    notifications: 'Notifications',
    enableNotifications: 'Enable Notifications',
    receiveNotifications: 'Receive app notifications',
    general: 'General',
    autoSave: 'Auto-Save',
    autoSaveDesc: 'Automatically save your changes',
    language: 'Language',
    selectLanguage: 'Select your preferred language',
    displaySettings: 'Display Settings',
    textSize: 'Text Size',
    chooseTextSize: 'Choose your preferred text size',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    voiceSelection: 'Voice Selection',
    preferredVoice: 'Preferred Voice',
    chooseVoice: 'Choose a voice for your coach',

    // Sidebar
    dashboard: 'My Dashboard',
    notificationsMenu: 'Notifications',
    coach: 'My Coach',
    documents: 'My Records',
    account: 'Account Management',
    progress: 'Daily Progress',
    foodLog: 'Food Log',
    charts: 'Metrics',

    // Theme names
    ocean: 'Ocean',
    midnight: 'Midnight',
    forest: 'Forest',
    sunset: 'Sunset',
    nord: 'Nord',
    dracula: 'Dracula',

    // Language names
    english: 'English',
    spanish: 'Spanish',
    chinese: 'Chinese',

    // Charts/Metrics page
    avgGlucose: 'Avg Glucose',
    latestA1C: 'Latest A1C',
    dailySteps: 'Daily Steps',
    currentWeight: 'Current Weight',
    bloodGlucoseLevels: 'Blood Glucose Levels',
    fasting: 'Fasting',
    postMeal: 'Post-Meal',
    target: 'Target',
    a1cTrend: 'A1C Trend',
    lastSixMonths: 'Last 6 Months',
    dailyActivity: 'Daily Activity',
    stepsPerDay: 'Steps per Day',
    medicationAdherence: 'Medication Adherence',
    lastThirtyDays: 'Last 30 Days',
    weightTrend: 'Weight Trend',
    lastFourWeeks: 'Last 4 Weeks',
    adherence: 'Adherence',
    dosesTaken: 'Doses Taken',
    dosesMissed: 'Doses Missed',

    // Dashboard
    welcomeBack: 'Welcome back, {name}',
    healthOverview: "Here's your health overview for today",
    quickActions: 'Quick Actions',
    logProgress: 'Log Progress',
    askHealthCoach: 'Ask Health Coach',
    viewDocuments: 'View Records',
    recentNotifications: 'Recent Notifications',
    viewAll: 'View All',
    upcomingAppointments: 'Upcoming Appointments',
    quarterlyCheckup: 'Quarterly Check-up',
    tomorrowAt: 'Tomorrow at',
    quarterlyBloodTests: 'Quarterly Blood Tests',
    mondayAt: 'Monday at',

    // My Records
    myDocumentsPage: 'My Records',
    allDocuments: 'All Records',
    recentBloodwork: 'Recent Bloodwork',
    patientReferrals: 'Patient Referrals',
    prescriptions: 'Prescriptions',
    medicalReports: 'Medical Reports',
    uploadDocument: 'Upload Record',
    document: 'record',
    documentPlural: 'records',

    // Account Management
    accountManagement: 'Account Management',
    personalInformation: 'Personal Information',
    fullName: 'Full Name',
    email: 'Email',
    phoneNumber: 'Phone Number',
    dateOfBirth: 'Date of Birth',
    medicalHistory: 'Medical History',
    conditions: 'Conditions',
    allergies: 'Allergies',
    medications: 'Medications',
    emergencyContact: 'Emergency Contact',
    contactName: 'Contact Name',
    relationship: 'Relationship',
    contactPhone: 'Contact Phone',

    // Daily Progress
    dailyProgress: 'Daily Progress',
    logYourProgress: 'Log Your Progress',
    bloodGlucose: 'Blood Glucose',
    mgDL: 'mg/dL',
    weight: 'Weight',
    lbs: 'lbs',
    steps: 'Steps',
    notes: 'Notes',
    submit: 'Submit',

    // Notifications
    notificationsTitle: 'Notifications',
    markAsRead: 'Mark as Read',
    clearAll: 'Clear All',

    // My Coach
    myCoach: 'My Coach',
    sendMessage: 'Send Message',
    message: 'Message',
    send: 'Send',
    recentMessages: 'Recent Messages',

    // Profile
    profile: 'Profile',
    editProfile: 'Edit Profile',
    save: 'Save',
    cancel: 'Cancel',
  },
  es: {
    // Settings
    settings: 'Configuración',
    managePreferences: 'Gestiona tus preferencias de la aplicación',
    appearance: 'Apariencia',
    theme: 'Tema',
    chooseTheme: 'Elige tu tema preferido',
    notifications: 'Notificaciones',
    enableNotifications: 'Habilitar Notificaciones',
    receiveNotifications: 'Recibir notificaciones de la aplicación',
    general: 'General',
    autoSave: 'Guardado Automático',
    autoSaveDesc: 'Guardar automáticamente tus cambios',
    language: 'Idioma',
    selectLanguage: 'Selecciona tu idioma preferido',
    displaySettings: 'Ajustes de Pantalla',
    textSize: 'Tamaño de Texto',
    chooseTextSize: 'Elige tu tamaño de texto preferido',
    small: 'Pequeño',
    medium: 'Mediano',
    large: 'Grande',
    voiceSelection: 'Selección de Voz',
    preferredVoice: 'Voz Preferida',
    chooseVoice: 'Elige una voz para tu entrenador',

    // Sidebar
    dashboard: 'Mi Panel',
    notificationsMenu: 'Notificaciones',
    coach: 'Mi Entrenador',
    documents: 'Mis Documentos',
    account: 'Gestión de Cuenta',
    progress: 'Progreso Diario',
    foodLog: 'Registro de Alimentos',
    charts: 'Métricas',

    // Theme names
    ocean: 'Océano',
    midnight: 'Medianoche',
    forest: 'Bosque',
    sunset: 'Atardecer',
    nord: 'Nord',
    dracula: 'Drácula',

    // Language names
    english: 'Inglés',
    spanish: 'Español',
    chinese: '中文',

    // Charts/Metrics page
    avgGlucose: 'Glucosa Promedio',
    latestA1C: 'Último A1C',
    dailySteps: 'Pasos Diarios',
    currentWeight: 'Peso Actual',
    bloodGlucoseLevels: 'Niveles de Glucosa en Sangre',
    fasting: 'En Ayunas',
    postMeal: 'Después de Comer',
    target: 'Objetivo',
    a1cTrend: 'Tendencia A1C',
    lastSixMonths: 'Últimos 6 Meses',
    dailyActivity: 'Actividad Diaria',
    stepsPerDay: 'Pasos por Día',
    medicationAdherence: 'Adherencia a Medicamentos',
    lastThirtyDays: 'Últimos 30 Días',
    weightTrend: 'Tendencia de Peso',
    lastFourWeeks: 'Últimas 4 Semanas',
    adherence: 'Adherencia',
    dosesTaken: 'Dosis Tomadas',
    dosesMissed: 'Dosis Perdidas',

    // Dashboard
    welcomeBack: 'Bienvenido de nuevo, {name}',
    healthOverview: 'Aquí está tu resumen de salud para hoy',
    quickActions: 'Acciones Rápidas',
    logProgress: 'Registrar Progreso',
    askHealthCoach: 'Preguntar al Entrenador de Salud',
    viewDocuments: 'Ver Documentos',
    recentNotifications: 'Notificaciones Recientes',
    viewAll: 'Ver Todo',
    upcomingAppointments: 'Próximas Citas',
    quarterlyCheckup: 'Revisión Trimestral',
    tomorrowAt: 'Mañana a las',
    quarterlyBloodTests: 'Pruebas de Sangre Trimestrales',
    mondayAt: 'Lunes a las',

    // My Documents
    myDocumentsPage: 'Mis Documentos',
    allDocuments: 'Todos los Documentos',
    recentBloodwork: 'Análisis de Sangre Recientes',
    patientReferrals: 'Derivaciones de Pacientes',
    prescriptions: 'Recetas',
    medicalReports: 'Informes Médicos',
    uploadDocument: 'Cargar Documento',
    document: 'documento',
    documentPlural: 'documentos',

    // Account Management
    accountManagement: 'Gestión de Cuenta',
    personalInformation: 'Información Personal',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    phoneNumber: 'Número de Teléfono',
    dateOfBirth: 'Fecha de Nacimiento',
    medicalHistory: 'Historial Médico',
    conditions: 'Condiciones',
    allergies: 'Alergias',
    medications: 'Medicamentos',
    emergencyContact: 'Contacto de Emergencia',
    contactName: 'Nombre del Contacto',
    relationship: 'Relación',
    contactPhone: 'Teléfono del Contacto',

    // Daily Progress
    dailyProgress: 'Progreso Diario',
    logYourProgress: 'Registra tu Progreso',
    bloodGlucose: 'Glucosa en Sangre',
    mgDL: 'mg/dL',
    weight: 'Peso',
    lbs: 'lbs',
    steps: 'Pasos',
    notes: 'Notas',
    submit: 'Enviar',

    // Notifications
    notificationsTitle: 'Notificaciones',
    markAsRead: 'Marcar como Leído',
    clearAll: 'Limpiar Todo',

    // My Coach
    myCoach: 'Mi Entrenador',
    sendMessage: 'Enviar Mensaje',
    message: 'Mensaje',
    send: 'Enviar',
    recentMessages: 'Mensajes Recientes',

    // Profile
    profile: 'Perfil',
    editProfile: 'Editar Perfil',
    save: 'Guardar',
    cancel: 'Cancelar',
  },
  zh: {
    // Settings
    settings: '设置',
    managePreferences: '管理您的应用偏好设置',
    appearance: '外观',
    theme: '主题',
    chooseTheme: '选择您喜欢的主题',
    notifications: '通知',
    enableNotifications: '启用通知',
    receiveNotifications: '接收应用通知',
    general: '常规',
    autoSave: '自动保存',
    autoSaveDesc: '自动保存您的更改',
    language: '语言',
    selectLanguage: '选择您喜欢的语言',
    displaySettings: '显示设置',
    textSize: '文字大小',
    chooseTextSize: '选择您喜欢的文字大小',
    small: '小',
    medium: '中',
    large: '大',
    voiceSelection: '语音选择',
    preferredVoice: '首选语音',
    chooseVoice: '为您的教练选择一个语音',

    // Sidebar
    dashboard: '我的仪表板',
    notificationsMenu: '通知',
    coach: '我的教练',
    documents: '我的文档',
    account: '账户管理',
    progress: '每日进度',
    foodLog: '食物记录',
    charts: '指标',

    // Theme names
    ocean: '海洋',
    midnight: '午夜',
    forest: '森林',
    sunset: '日落',
    nord: 'Nord',
    dracula: '德古拉',

    // Language names
    english: 'English',
    spanish: 'Español',
    chinese: '中文',

    // Charts/Metrics page
    avgGlucose: '平均血糖',
    latestA1C: '最新A1C',
    dailySteps: '每日步数',
    currentWeight: '当前体重',
    bloodGlucoseLevels: '血糖水平',
    fasting: '空腹',
    postMeal: '餐后',
    target: '目标',
    a1cTrend: 'A1C趋势',
    lastSixMonths: '过去6个月',
    dailyActivity: '每日活动',
    stepsPerDay: '每天步数',
    medicationAdherence: '用药依从性',
    lastThirtyDays: '过去30天',
    weightTrend: '体重趋势',
    lastFourWeeks: '过去4周',
    adherence: '依从性',
    dosesTaken: '已服用剂量',
    dosesMissed: '遗漏剂量',

    // Dashboard
    welcomeBack: '欢迎回来，{name}',
    healthOverview: '这是您今天的健康概览',
    quickActions: '快速操作',
    logProgress: '记录进度',
    askHealthCoach: '询问健康教练',
    viewDocuments: '查看文档',
    recentNotifications: '最近通知',
    viewAll: '查看全部',
    upcomingAppointments: '即将到来的预约',
    quarterlyCheckup: '季度检查',
    tomorrowAt: '明天在',
    quarterlyBloodTests: '季度血液检查',
    mondayAt: '周一在',

    // My Documents
    myDocumentsPage: '我的文档',
    allDocuments: '所有文档',
    recentBloodwork: '最近血液检查',
    patientReferrals: '患者转诊',
    prescriptions: '处方',
    medicalReports: '医疗报告',
    uploadDocument: '上传文档',
    document: '文档',
    documentPlural: '文档',

    // Account Management
    accountManagement: '账户管理',
    personalInformation: '个人信息',
    fullName: '全名',
    email: '电子邮件',
    phoneNumber: '电话号码',
    dateOfBirth: '出生日期',
    medicalHistory: '医疗历史',
    conditions: '条件',
    allergies: '过敏症',
    medications: '药物',
    emergencyContact: '紧急联系人',
    contactName: '联系人名称',
    relationship: '关系',
    contactPhone: '联系电话',

    // Daily Progress
    dailyProgress: '每日进度',
    logYourProgress: '记录您的进度',
    bloodGlucose: '血糖',
    mgDL: 'mg/dL',
    weight: '体重',
    lbs: '磅',
    steps: '步数',
    notes: '笔记',
    submit: '提交',

    // Notifications
    notificationsTitle: '通知',
    markAsRead: '标记为已读',
    clearAll: '全部清除',

    // My Coach
    myCoach: '我的教练',
    sendMessage: '发送消息',
    message: '消息',
    send: '发送',
    recentMessages: '最近消息',

    // Profile
    profile: '个人资料',
    editProfile: '编辑个人资料',
    save: '保存',
    cancel: '取消',
  },
};

export const getTranslation = (language: Language, key: string, params?: Record<string, string>): string => {
  let text = translations[language]?.[key] || translations['en'][key] || key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
  }
  return text;
};
